import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { patientsApi } from '@/api/patients';
import AssignPatientSelectStep from '@/components/assignments/steps/AssignPatientSelectStep';
import { AppDialogLayout } from '@/components/system';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import { useDebounce } from '@/hooks/use-debounce';
import { usePatientAnamnesis } from '@/hooks/use-patient-anamnesis';
import AnamnesisStartStep from './AnamnesisStartStep';
import { describePatient } from './resolve-template';
import { describeAnamnesis } from './utils';
import type { AnamnesisTemplateId } from './templates';
import type { AssignPatientListItem } from '@/components/assignments/steps/AssignPatientSelectStep';
import type { Patient } from '@/types/patient';

type Props = {
    /**
     * Navigates to the editor. Provided by the caller because this wizard renders
     * inside the modal layer, which sits outside the router — router hooks throw here.
     */
    onOpenEditor: (
        patientId: string,
        templateId?: AnamnesisTemplateId,
        replaceExisting?: boolean,
    ) => void;
};

/**
 * The global "Nova anamnese" flow, opened from the sidebar's create menu: pick a
 * patient, then choose how to start. The patient page reaches the same start step
 * directly (it already knows the patient), so this only adds the picker and the
 * "already has an anamnesis" fork.
 *
 * Owns the dialog chrome (title, patient subtitle and the one back button) so the
 * steps below never render a back control of their own.
 */
export default function NewAnamnesisWizard({ onOpenEditor }: Props) {
    const [patient, setPatient] = useState<Patient | null>(null);
    const [showGallery, setShowGallery] = useState(false);
    const [showUpload, setShowUpload] = useState(false);

    const selectPatient = (next: Patient) => {
        setPatient(next);
        setShowGallery(false);
        setShowUpload(false);
    };

    // One control, walking back through the steps in order.
    const back = showUpload
        ? () => setShowUpload(false)
        : showGallery
          ? () => setShowGallery(false)
          : patient
            ? () => setPatient(null)
            : undefined;

    return (
        <AppDialogLayout
            title="Nova anamnese"
            description={patient ? describePatient(patient) : 'Para qual paciente?'}
            backAction={back ? { label: 'Voltar', onClick: back } : undefined}
        >
            {patient ? (
                <StartStep
                    patient={patient}
                    showGallery={showGallery}
                    onShowGallery={() => setShowGallery(true)}
                    showUpload={showUpload}
                    onShowUpload={() => setShowUpload(true)}
                    onOpenEditor={onOpenEditor}
                />
            ) : (
                <PatientStep onSelect={selectPatient} />
            )}
        </AppDialogLayout>
    );
}

function PatientStep({ onSelect }: { onSelect: (patient: Patient) => void }) {
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 300);
    const normalizedSearchQuery = debouncedSearchQuery.trim();

    const {
        data: patients = [],
        isLoading,
        isFetching,
    } = useQuery({
        queryKey: ['patients', 'all', normalizedSearchQuery],
        queryFn: () => patientsApi.listAll({ q: normalizedSearchQuery || undefined }),
    });

    const items: AssignPatientListItem[] = patients.map((patient) => ({ patient }));

    return (
        <AssignPatientSelectStep
            patients={items}
            isLoading={isLoading || (isFetching && patients.length === 0)}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSelectPatient={onSelect}
        />
    );
}

function StartStep({
    patient,
    showGallery,
    onShowGallery,
    showUpload,
    onShowUpload,
    onOpenEditor,
}: {
    patient: Patient;
    showGallery: boolean;
    onShowGallery: () => void;
    showUpload: boolean;
    onShowUpload: () => void;
} & Props) {
    const { data: anamnesis, isLoading } = usePatientAnamnesis(patient.id);
    const [confirmingRestart, setConfirmingRestart] = useState(false);

    if (isLoading) return <Loading />;

    // Already written or uploaded: don't quietly start over on top of real work.
    if (anamnesis && !confirmingRestart) {
        return (
            <div className="space-y-4">
                <Alert variant="info">
                    {patient.full_name} já tem uma anamnese — {describeAnamnesis(anamnesis)}.
                </Alert>
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button variant="surface" onClick={() => setConfirmingRestart(true)}>
                        Recomeçar do zero
                    </Button>
                    <Button onClick={() => onOpenEditor(patient.id)}>Abrir anamnese</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {confirmingRestart && (
                <Alert variant="warning">
                    O conteúdo atual será substituído. Essa ação não pode ser desfeita.
                </Alert>
            )}

            <AnamnesisStartStep
                patientId={patient.id}
                patient={patient}
                showGallery={showGallery}
                onShowGallery={onShowGallery}
                showUpload={showUpload}
                onShowUpload={onShowUpload}
                onSelect={(templateId) => onOpenEditor(patient.id, templateId, confirmingRestart)}
                onUploaded={() => onOpenEditor(patient.id)}
            />
        </div>
    );
}
